import { useAuth } from "@clerk/clerk-react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { TemplateRubric, TemplateRubricField } from "schemas/admin";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import RubricCard from "./RubricCard";

const AdminTemplateRubrics = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rubrics, setRubrics] = useState<TemplateRubric[]>([]);

  const fetchRubrics = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/template-rubrics",
        "GET",
      );
      setRubrics(res.rubrics || []);
    } catch (error) {
      setAlert(error?.message || "Failed to load rubrics", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRubrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (
    rubricId: number,
    fields: TemplateRubricField[],
  ) => {
    setSaving(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/template-rubrics/${rubricId}/fields`,
        "PATCH",
        {},
        { fields },
      );
      setRubrics((prev) =>
        prev.map((r) => (r.id === rubricId ? res.rubric : r)),
      );
      setAlert("Rubric saved", "success");
    } catch (error) {
      setAlert(error?.message || "Failed to save rubric", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledPage
      content={
        <>
          <Typography variant="h3" gutterBottom sx={{ mt: 2 }}>
            <strong>Template Rubrics</strong>
          </Typography>

          {loading ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : (
            <Stack spacing={3} sx={{ mt: 2 }}>
              {rubrics.map((rubric) => (
                <RubricCard
                  key={rubric.id}
                  rubric={rubric}
                  onSave={handleSave}
                  saving={saving}
                />
              ))}
              {rubrics.length === 0 && (
                <Typography color="text.secondary">
                  No rubrics found.
                </Typography>
              )}
            </Stack>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </>
      }
    />
  );
};

export default AdminTemplateRubrics;
